<script>
import ENUMS from '@shared/messages/enums.json';

export default {
  messages: {
    status: ENUMS.membershipStatus,
    statusClass: ENUMS.membershipStatusClass,
  },
  props: {
    membership: {
      type: Object,
      required: true,
    },
    reorderable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    schoolUrl() {
      const q = `?p=${this.$store.state.auth.previewToken}`;
      return `${this.$store.getters['auth/url']}/mon-espace/abonnements/${this.membership.slug}${q}`;
    },
    salesUrl() {
      const { protocol } = window.location;
      return `${protocol}${this.$store.getters['auth/url']}/abonnements/${this.membership.slug}`;
    },
    pricesCount() {
      if (!Array.isArray(this.membership.prices)) {
        return 0;
      }

      return this.membership.prices.filter((price) => price.status === 'active').length;
    },
    pricesLabel() {
      if (this.pricesCount === 0) {
        return 'Aucun prix';
      }

      return `${this.pricesCount} prix`;
    },
  },
  methods: {
    updateStatus(status) {
      const loader = this.$buefy.loading.open();
      this.$store.dispatch('memberships/update', {
        uuid: this.membership.uuid,
        membershipData: { status },
      })
        .then(() => {
          const statusMessages = {
            PUBLISHED: 'Abonnement publié avec succès',
            DRAFT: 'Abonnement mis en brouillon',
            UNLISTED: 'Abonnement mis non répertorié',
            HIDDEN: 'Abonnement caché',
          };
          this.$buefy.toast.open({
            message: statusMessages[status] || 'Statut mis à jour avec succès',
            type: 'is-success',
            duration: 3000,
          });
        })
        .catch((error) => {
          const message = error.response?.data?.message || 'Une erreur est survenue lors de la mise à jour';
          this.$buefy.toast.open({
            message,
            type: 'is-danger',
            duration: 4000,
          });
        })
        .finally(() => loader.close());
    },
    deleteItem() {
      this.$buefy.dialog.confirm({
        title: 'Cette action est définitive !',
        message: `
          <div class="content">
            <p>
              <strong>Attention</strong> !
              Vous allez supprimer l'abonnement
              <strong>${this.membership.name}</strong>.
            </p>
            <p class="has-text-danger has-text-weight-bold">
              Cette action est définitive !
            </p>
          </div>
        `,
        type: 'is-danger',
        focusOn: 'cancel',
        onConfirm: () => {
          const loader = this.$buefy.loading.open();
          this.$store.dispatch('memberships/delete', this.membership)
            .finally(() => loader.close());
        },
      });
    },
  },
};
</script>

<template>
  <div class="tscomp box pb-0 pr-0 overflow-hidden">
    <article class="media">
      <b-button v-if="reorderable" class="handle media-left is-hidden-mobile" type="is-text" icon-left="grip-lines"
        title="Cliquez et glissez pour réodonner" />
      <router-link class="media-left image is-128x128 cursor-pointer bradius-8 is-hidden-mobile"
        :class="{ 'has-background-grey': !membership.picture }"
        :to="{ name: 'membership', params: { uuid: membership.uuid } }" tag="figure">
        <img v-if="membership.picture" class="ofit-cover bradius-8" :style="{ width: '128px', height: '128px' }"
          :src="membership.picture" :alt="membership.name">
      </router-link>
      <div class="media-content">
        <p class="is-size-8 mb-3">
          <span class="tag mr-3">
            <b-icon class="mr-1 is-size-9" icon="circle" :type="$options.messages.statusClass[membership.status]" />
            {{ $options.messages.status[membership.status] }}
          </span>
          <span>
            <time>Créé le <strong>{{ membership.created_at | momentFromUTC | moment('DD/MM/YY') }}</strong></time>

            <span class="mx-1">•</span>

            <time>Modifié le <strong>{{ membership.updated_at | momentFromUTC | moment('DD/MM/YY') }}</strong></time>
          </span>
        </p>
        <h2 class="is-size-5 has-text-weight-bold">
          <router-link class="has-text-black" :to="{ name: 'membership', params: { uuid: membership.uuid } }">
            {{ membership.name }}
          </router-link>
        </h2>

        <div class="is-flex pr-5 mt-7" style="gap: 6px;">
          <div v-for="(tag, index) in membership.tags" :key="index" @click="$emit('selected-tag', { tag })">
            <b-tag type="is-primary" style="cursor:pointer;">
              {{ tag }}
            </b-tag>
          </div>
        </div>
      </div>
      <nav class="media-right buttons is-align-self-center">
        <b-button tag="router-link" :to="{ name: 'membership', params: { uuid: membership.uuid } }">
          Modifier
        </b-button>
        <b-dropdown position="is-bottom-left" aria-role="menu" append-to-body>
          <template #trigger>
            <b-button type="is-text" icon-left="eye" />
          </template>
          <b-dropdown-item has-link aria-role="menuitem">
            <a :href="salesUrl" target="_blank">
              Voir la page visiteur
            </a>
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown class="mr-3" position="is-bottom-left" aria-role="menu" append-to-body>
          <template #trigger>
            <b-button type="is-text" icon-left="ellipsis-h" />
          </template>
          <b-dropdown-item class="has-text-danger" has-link aria-role="menuitem">
            <router-link :to="{ name: 'membership_settings', params: { uuid: membership.uuid } }">
              Paramètres
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item separator />
          <b-dropdown-item aria-role="menuitem" @click="updateStatus('PUBLISHED')">
            <b-icon icon="circle" type="is-success" class="is-size-9" />
            Publier
          </b-dropdown-item>
          <b-dropdown-item aria-role="menuitem" @click="updateStatus('UNLISTED')">
            <b-icon icon="circle" type="is-info" class="is-size-9" />
            Non répertorié
          </b-dropdown-item>
          <b-dropdown-item aria-role="menuitem" @click="updateStatus('HIDDEN')">
            <b-icon icon="circle" type="is-dark" class="is-size-9" />
            Cacher
          </b-dropdown-item>
          <b-dropdown-item aria-role="menuitem" @click="updateStatus('DRAFT')">
            <b-icon icon="circle" type="is-warning" class="is-size-9" />
            Mettre en brouillon
          </b-dropdown-item>
          <b-dropdown-item v-if="membership.status !== 'DRAFT'" separator />
          <b-dropdown-item v-if="membership.status !== 'DRAFT'" class="has-text-danger" has-link aria-role="menuitem">
            <router-link :to="{ name: 'marketing', query: { membership: membership.uuid } }">
              Générer un lien UTM
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item v-if="membership.status !== 'DRAFT'" class="has-text-danger" has-link aria-role="menuitem">
            <router-link :to="{ name: 'embeds', query: { membership: membership.uuid } }">
              Intégrer en widget
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item separator />
          <b-dropdown-item class="has-text-danger" aria-role="menuitem" @click="deleteItem">
            Supprimer
          </b-dropdown-item>
        </b-dropdown>
      </nav>
    </article>
    <footer class="-mx-5 mt-5 px-5 py-3 btop-grey">
      <p class="has-text-grey-dark">
        <span class="is-inline-block is-size-8">
          <b-icon icon="tag" />
          {{ pricesLabel }}
        </span>
        <span class="is-inline-block is-size-8 ml-4">
          <b-icon icon="list" />
          {{ membership.items ? membership.items.length : 0 }}
          {{ (membership.items ? membership.items.length : 0) > 1 ? 'contenus' : 'contenu' }}
        </span>
        <span class="is-inline-block is-size-8 ml-4">
          <b-icon icon="users" />
          {{ membership.active_subscribers_count || 0 }}
          {{ (membership.active_subscribers_count || 0) > 1 ? 'abonnés' : 'abonné' }}
        </span>
      </p>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.tscomp {
  @include mobile {
    padding-right: pxToRem(20px) !important;

    .media {
      flex-wrap: wrap;
    }

    .media-right {
      width: 100%;
      margin: pxToRem(20px) 0 0;
    }
  }
}

footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;

  @include mobile {
    flex-direction: column;
  }
}
</style>
